import { defineMessages } from 'react-intl';

import type { PilotId } from '~/eds/src/types/PilotId';
import type { SavedSearch } from '~/features/search/types/search';
import type { EntityQuery } from '~/types/OpenSearch';

import { showToast } from '~/components/App/toast/showToast';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import {
  RESERVED_SCOPE_NAME,
  type HomeSettings,
} from '~/features/home/utils/homeSettings';
import { intlService } from '~/i18n/IntlService';
import { ServiceRequestConfig } from '~/services/createService';

import { getSavedSearches } from '../searchV3/getSavedSearches';
import { postSavedSearch } from '../searchV3/postSavedSearch';
import { putSavedSearch } from '../searchV3/putSavedSearch';
import { SavedSearchRequestError } from '../searchV3/SavedSearchRequestError';
import { updateHomeSettings } from '../userPreferences';
import { serializeScopeWrite } from './serializeScopeWrite';
const SCOPE_LOOKUP_PAGE_SIZE = 100;

const saveSuccessToast = {
  id: 'useHomeSettings.saveSuccess',
  defaultMessage: 'Home settings saved.',
  description: 'Success toast shown after saving Home settings',
};

const messages = defineMessages({
  savedSearchError: {
    id: 'Home.Scope.savedSearch.error',
    defaultMessage: 'We could not update your Home scope.',
    description:
      'Home scope: toast shown when the reserved saved search cannot be written',
  },
  appliedPointed: {
    id: 'Home.Scope.applied.pointed',
    defaultMessage: 'Applied {name} to Home.',
    description:
      'Home scope: toast shown after Home is pointed at an unedited saved filter, naming it',
  },
  appliedSaved: {
    id: 'Home.Scope.applied.saved',
    defaultMessage: 'Applied to Home. Saved as {name} in Saved filters.',
    description:
      'Home scope: toast shown after the filters are applied to Home and saved as a new saved filter, naming the stored filter',
  },
});

function appliedToast(isPoint: boolean, name: string) {
  return intlService
    .getIntl()
    .formatMessage(isPoint ? messages.appliedPointed : messages.appliedSaved, {
      name,
    });
}

export type ApplyHomeScopeRequest = {
  settings: HomeSettings;
  description: string;
  query: EntityQuery[];
  name?: string;
  savedSearchId?: PilotId;
};

// Bounds the POSTs: after the first clash the saved-search list picks the
// suffix, and a clash it cannot predict (a dashboard, another tab) moves on.
const MAX_NAME_ATTEMPTS = 10;

const NAME_TAKEN_CODE = 'dashboard_exists';

const suffixedName = (name: string, number: number) =>
  number === 1 ? name : `${name} (${number})`;

const testIsNameTaken = (error: unknown) =>
  error instanceof SavedSearchRequestError && error.code === NAME_TAKEN_CODE;

export async function writeHomeScopePreference(
  homeSettings: HomeSettings,
  options: ServiceRequestConfig = {},
) {
  await updateHomeSettings(
    { homeSettings },
    {
      ...options,
      showSuccessToast:
        options.showSuccessToast ??
        intlService.getIntl().formatMessage(saveSuccessToast),
      showErrorToast: options.showErrorToast ?? true,
    },
  );
}

const testIsReservedScopeSearch = (search: SavedSearch) =>
  search.name === RESERVED_SCOPE_NAME &&
  search.visibility === SaveSearchPermissionType.Private &&
  // The list merges in v2 searches, whose ids the v3 PUT would not resolve.
  search.version === 3;

async function* mySavedSearchPages() {
  const listPage = (page: number) =>
    getSavedSearches({
      page,
      pageSize: SCOPE_LOOKUP_PAGE_SIZE,
      types: ['mine'],
    });

  const { results, meta } = await listPage(1);
  yield results;

  const pageCount = Math.ceil(meta.total / SCOPE_LOOKUP_PAGE_SIZE);
  for (let page = 2; page <= pageCount; page += 1) {
    yield (await listPage(page)).results;
  }
}

async function findReservedScopeSearchId() {
  try {
    // The list is newest-first and the reserved search is created once and PUT
    // forever after, so it sinks off page 1 as the user saves more.
    for await (const results of mySavedSearchPages()) {
      const found = results.find(testIsReservedScopeSearch);
      if (found) {
        return found.id;
      }
    }
    return null;
  } catch {
    return null;
  }
}

async function listTakenNames() {
  const names = new Set<string>();
  try {
    for await (const results of mySavedSearchPages()) {
      results.forEach((search) => names.add(search.name));
    }
  } catch {
    // Without the list, the POST clashes alone decide the next suffix.
  }
  return names;
}

export function applyHomeScope(
  request: ApplyHomeScopeRequest,
  options: ServiceRequestConfig = {},
) {
  return serializeScopeWrite(async (currentSettings) => {
    const { description, query } = request;
    const settings = currentSettings(request.settings);
    const agentName = request.name?.trim() ?? '';
    const payload = {
      name: agentName || RESERVED_SCOPE_NAME,
      query,
      filters: [],
      resultTable: [],
      visibility: SaveSearchPermissionType.Private,
    };
    const writeAsNew = async () => {
      // Most names are free, so POST the plain name first and only read the
      // saved-search list after a clash.
      let taken: Set<string> | null = null;
      let number = 1;
      for (let attempt = 0; attempt < MAX_NAME_ATTEMPTS; attempt += 1) {
        const name = suffixedName(agentName, number);
        try {
          return await postSavedSearch({ ...payload, name });
        } catch (error) {
          if (!testIsNameTaken(error)) {
            throw error;
          }
          taken ??= await listTakenNames();
          taken.add(name);
          number += 1;
          while (taken.has(suffixedName(agentName, number))) {
            number += 1;
          }
        }
      }
      // Pilot words this clash for dashboards; Home shows its own message.
      throw new Error(
        intlService.getIntl().formatMessage(messages.savedSearchError),
      );
    };
    // The `(user, name)` uniqueness constraint spans dashboards, so a blind
    // POST of the reserved name 400s `dashboard_exists` for anyone who has
    // applied a scope before.
    const writeByName = async () => {
      const found = await findReservedScopeSearchId();
      if (found != null) {
        return putSavedSearch({ id: found, savedSearch: payload });
      }
      try {
        return await postSavedSearch(payload);
      } catch (error) {
        // The name can be taken by something this list cannot show -- an
        // analytics dashboard, or a search another tab just created -- so
        // one more lookup decides between a stale miss and a real clash.
        const recovered = await findReservedScopeSearchId();
        if (recovered == null) {
          throw error;
        }
        return putSavedSearch({
          id: recovered,
          savedSearch: payload,
        });
      }
    };

    const write = async () => {
      if (agentName) {
        return writeAsNew();
      }
      const pointerId = settings.scope?.savedSearchId;
      if (pointerId == null || settings.scope?.name !== RESERVED_SCOPE_NAME) {
        return writeByName();
      }
      try {
        return await putSavedSearch({
          id: pointerId,
          savedSearch: payload,
        });
      } catch {
        // The pointer can outlive its search, and re-deriving once is
        // enough: a second miss means the name is genuinely free.
        return writeByName();
      }
    };

    const writeAndToast = async () => {
      try {
        const saved = await write();
        if (saved?.id == null) {
          throw new Error(
            intlService.getIntl().formatMessage(messages.savedSearchError),
          );
        }
        return saved;
      } catch (error) {
        if (error instanceof Error && error.message) {
          showToast({ status: 'critical', text: error.message });
        }
        throw error;
      }
    };

    const saved =
      request.savedSearchId != null
        ? { id: request.savedSearchId, name: agentName }
        : await writeAndToast();

    // Saved search first, preference second, so a failed second write
    // leaves the user unscoped rather than scoped-but-unrecorded. The
    // residue is a saved search whose id nobody stored, which the next
    // apply recovers by name.
    const next = {
      ...settings,
      scope: {
        savedSearchId: saved.id,
        name: saved.name || payload.name,
        description,
        query,
      },
    };
    await writeHomeScopePreference(next, {
      ...options,
      showSuccessToast:
        options.showSuccessToast ??
        appliedToast(request.savedSearchId != null, next.scope.name),
    });
    return { settings: next };
  });
}
